import type { V1Event } from '@/generated/hooks/rain/models';

/**
 * Sentry `info` logging for the Rain thread SSE stream
 * (`.../agents/{agentId}/threads/{threadId}/stream`).
 *
 * Logs the envelope only — no payload field is read, so nothing the event
 * carries (prompts, model output, reasoning, tool arguments, artifact content)
 * can reach Sentry.
 *
 * The event type is derived from the payload's shape rather than matched
 * against a list of known fields, so an event type the backend adds later is
 * named correctly with no change here. A V1Event is a oneof-style wrapper:
 * envelope fields are scalars (`id`, `task_id`, `recorded_at_ms`, `deleted`)
 * and the single populated payload field is a nested message object. Finding
 * the object-valued key therefore identifies the event without naming any
 * field in this file.
 */

/** Envelope metadata describing one received event. Never includes payload. */
export interface IRainEventLogMeta {
  /**
   * The event's identifier: `event.id` when the server assigned one, else the
   * SSE frame id.
   *
   * `event.id` is a thread sequence id set only for persisted events; ephemeral
   * notifications have none (and no `recorded_at_ms` either). The SSE frame id
   * is an independent transport-level fallback, so this is populated for any
   * event arriving over the SSE stream.
   */
  eventId?: string;
  /** Derived payload field name, or 'unknown' for an envelope-only event. */
  eventType: string;
  /** Sub-agent task this event belongs to; absent at root scope. */
  taskId?: string;
  /** Server-assigned record time, when present (persisted events only). */
  recordedAtMs?: string;
  /** Only set when the event belongs to a deleted turn. */
  deleted?: true;
}

/**
 * Names the event by finding its populated payload field.
 *
 * Payload fields hold nested message objects; envelope fields hold scalars. So
 * the first object-valued own key is the event type — no field names are
 * hardcoded, and a payload field added to the proto names itself.
 *
 * Returns 'unknown' when no object-valued field is present (an envelope-only
 * event, or a payload shape this heuristic does not fit) — worth seeing in the
 * logs either way.
 */
export const getRainEventType = (event: V1Event): string => {
  for (const [key, value] of Object.entries(event)) {
    if (typeof value === 'object' && value !== null) return key;
  }
  return 'unknown';
};

/**
 * Builds the envelope metadata for one received event.
 *
 * `frameEventId` is the SSE frame id from the stream client, used when the event
 * itself carries no `id`.
 */
export const buildRainEventLogMeta = (
  event: V1Event,
  frameEventId?: string,
): IRainEventLogMeta => ({
  eventId: event.id ?? frameEventId,
  eventType: getRainEventType(event),
  ...(event.task_id !== undefined ? { taskId: event.task_id } : {}),
  ...(event.recorded_at_ms !== undefined
    ? { recordedAtMs: event.recorded_at_ms }
    : {}),
  ...(event.deleted ? { deleted: true as const } : {}),
});
